"use client";
import { getMask, useImageExtraContext } from "@/hooks/useImageExtraContext";
import { cn } from "@/lib/utils";
import { BASE_URL } from "@/utils/APIRoutes";
import { BoundingBoxFromAPI, ProductDetail } from "@/utils/APIRouteTypes";
import { Properties } from "@/utils/properties";
import Image, { ImageProps } from "next/image";
import React, { useId, useRef } from "react";
import TShirt from "../others/tshirt";
import { TextOnlyPreview } from "./textPreview";
import NoiseImg from "@/public/noise.png"

type AggregatedImageProps = {
  side: "front" | "back";
  imgSrc: string;
  boundingBox: BoundingBoxFromAPI;
  imgHeight: number
  imgWidth: number
  tshirtAltText: string
  onClick?: () => void;
  className?: string
  multiplyImage?: ProductDetail['frontOutline'] | ProductDetail['backOutline']
};

export default function AggregatedImage(props: AggregatedImageProps) {
  const tshirtRef = React.useRef<HTMLDivElement>(null);
  const { ImgContext, blendMode, filter } = useImageExtraContext();
  const imgSrc = props.imgSrc.startsWith('/') ? BASE_URL + props.imgSrc : props.imgSrc
  const mask = getMask(props.side, ImgContext)
  const filterID = useId()
  const contextKey1 = props.side === "front" ? "frontImages" : "backImages";

  const handleClick = () => {
    if (props.onClick)
      props.onClick()
  }

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (e.currentTarget.naturalWidth > e.currentTarget.naturalHeight) {
      e.currentTarget.style.width = "100% !important";
      e.currentTarget.style.minWidth = "100%";
      e.currentTarget.style.height = "auto";
      e.currentTarget.style.aspectRatio = `${e.currentTarget.naturalWidth} / ${e.currentTarget.naturalHeight}`;
    }
    else {
      e.currentTarget.style.height = "100% !important";
      e.currentTarget.style.minHeight = "100%";
      e.currentTarget.style.width = "auto";
      e.currentTarget.style.aspectRatio = `${e.currentTarget.naturalWidth} / ${e.currentTarget.naturalHeight}`;
    }
  }

  return (
    <div className={cn("relative flex size-full items-center justify-center bg-white", props.className)}>
      <div className="absolute top-0 left-0 size-full z-10 mix-blend-overlay pointer-events-none">
        <Image
          src={NoiseImg}
          alt="Noise"
          layout="fill"
          objectFit="cover"
          objectPosition="center"
          draggable={false}
          loading="eager"
        />
      </div>
      <div ref={tshirtRef} className="relative aggregated-image w-full" onClick={handleClick} data-track="Aggregated Image" style={{ aspectRatio: `${props.imgWidth} / ${props.imgHeight}` }}>
        {props.multiplyImage && <ImageWithDisplacementMap
          alt=""
          filterScale="6"
          filterID={filterID}
          src={props.multiplyImage}
          width={props.imgWidth}
          height={props.imgHeight}
          className="absolute top-0 left-0 z-40 mix-blend-multiply object-contain"
        />
        }
        <div className={"absolute top-0 left-0 size-full " + blendMode}
          style={{ filter: `url(#${filterID})` }}
        >
          <div
            className={
              `group absolute overflow-hidden transition-all duration-75 opacity-85`
            }
            style={{
              top: `${props.boundingBox.y * 100}%`,
              left: `${props.boundingBox.x * 100}%`,
              width: `${props.boundingBox.width * 100}%`,
              height: `${props.boundingBox.height * 100}%`,
            }}
          >
            {ImgContext.textBoxes?.filter(x => x.side === props.side).map((textBox, index) => (
              <TextOnlyPreview
                textBox={textBox}
                key={index}
              />
            ))}
            <div className="size-full z-50" style={{
              maskImage: mask,
              maskSize: '100% 100%',
            }}>
              {ImgContext[contextKey1].map((img, index) => {
                const boundingBoxRealHeight = props.boundingBox.height * props.imgHeight
                const boundingBoxRealWidth = props.boundingBox.width * props.imgWidth
                const ImgWidth = `${img.scale * Properties.defaultImgWidth}%`;
                const ImgHeight = `${img.scale * Properties.defaultImgWidth * boundingBoxRealWidth / boundingBoxRealHeight}%`;
                const ImgTop = `${img.y * 100}%`;
                const ImgLeft = `${img.x * 100}%`;

                return (
                  <div
                    className="absolute z-30 flex items-center justify-center overflow-hidden object-contain transition-all"
                    style={{
                      width: ImgWidth,
                      top: ImgTop,
                      left: ImgLeft,
                      height: ImgHeight,
                      maskImage: mask,
                      maskSize: 'cover',
                    }}
                    key={index}
                  >
                    {img.src.length > 0 && <Image
                      src={img.src}
                      style={{
                        maskImage: mask,
                        maskSize: '100% 100%',
                        filter: filter,

                      }}
                      alt="Generated Image"
                      className='max-h-full max-w-full opacity-100 !w-auto !h-auto z-30'
                      loading="eager"
                      key={index}
                      layout="responsive"
                      draggable={false}
                      width={200}
                      onLoad={handleImageLoad}
                      height={0}
                      onContextMenu={(e) => e.preventDefault()}
                    />
                    }
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <TShirt src={imgSrc} altText={props.tshirtAltText} height={props.imgHeight} width={props.imgWidth} />
      </div>
    </div>
  );
}


export const ImageWithDisplacementMap = (props: ImageProps & { filterID: string, bypassUACheck?: boolean, filterScale?: string }) => {
  const imgRef = useRef<HTMLImageElement>(null)

  const isAcceptableUserAgent = () => {
    if (typeof window.navigator === 'undefined') return false
    const userAgent = window.navigator.userAgent.toLowerCase()
    if (userAgent.includes('safari')) {
      if ((userAgent.includes('macintosh') && userAgent.includes('chrome')) || userAgent.includes('android'))
        return true
      return false
    }
    return true
  }

  const isAcceptable = isAcceptableUserAgent()

  return (
    <>
      {(isAcceptable || props.bypassUACheck) && <svg xmlns="http://www.w3.org/2000/svg" className="absolute top-0 left-0 size-full">
        <filter id={props.filterID} filterUnits="userSpaceOnUse" x="0" y="0" width="100%" height="100%">
          <feImage
            width="100%"
            height="100%"
            preserveAspectRatio="none"
            href={imgRef.current?.src}
            result="displacementMap"
          />
          <feGaussianBlur in="displacementMap" stdDeviation="0.8" result="blurredMap" />
          <feComponentTransfer in="blurredMap" result="adjustedMap">
            <feFuncR type="linear" slope="0.8" intercept="0" />
            <feFuncG type="linear" slope="0.8" intercept="0" />
            <feFuncB type="linear" slope="0.8" intercept="0" />
          </feComponentTransfer>

          <feDisplacementMap in="SourceGraphic" in2="adjustedMap" scale={props.filterScale || "15"} xChannelSelector="R" yChannelSelector="G" result="distortedImage" />
        </filter>
      </svg>}
      <Image {...props} ref={imgRef} />
    </>
  )
}